type WatchAlertCounter = { id: string; alerts: number };

export function countNewWatchAlerts(
  previous: ReadonlyMap<string, number> | undefined,
  current: WatchAlertCounter[],
): number {
  if (!previous) return 0;
  return current.reduce((total, watch) =>
    total + Math.max(0, watch.alerts - (previous.get(watch.id) ?? 0)), 0);
}

/** Une note : fréquence en Hz, début et durée en secondes, relatives au départ du son. */
type Note = { frequency: number; start: number; duration: number; type?: OscillatorType; gain?: number };

export const ALERT_SOUNDS = {
  chime: { label: "Carillon", description: "Deux notes montantes, discrètes.", notes: [
    { frequency: 740, start: 0, duration: 0.14 },
    { frequency: 880, start: 0.12, duration: 0.2 },
  ] },
  radar: { label: "Radar", description: "Trois bips courts et nets.", notes: [
    { frequency: 1320, start: 0, duration: 0.08, type: "square", gain: 0.45 },
    { frequency: 1320, start: 0.14, duration: 0.08, type: "square", gain: 0.45 },
    { frequency: 1320, start: 0.28, duration: 0.08, type: "square", gain: 0.45 },
  ] },
  bell: { label: "Cloche", description: "Un coup de cloche qui résonne.", notes: [
    { frequency: 1046.5, start: 0, duration: 0.9 },
    { frequency: 2093, start: 0, duration: 0.5, gain: 0.35 },
    { frequency: 3136, start: 0, duration: 0.3, gain: 0.15 },
  ] },
  alarm: { label: "Alarme", description: "Sirène alternée, difficile à manquer.", notes: [
    { frequency: 880, start: 0, duration: 0.16, type: "sawtooth", gain: 0.35 },
    { frequency: 660, start: 0.16, duration: 0.16, type: "sawtooth", gain: 0.35 },
    { frequency: 880, start: 0.32, duration: 0.16, type: "sawtooth", gain: 0.35 },
    { frequency: 660, start: 0.48, duration: 0.16, type: "sawtooth", gain: 0.35 },
  ] },
  arpeggio: { label: "Arpège", description: "Accord montant, plus mélodique.", notes: [
    { frequency: 523.25, start: 0, duration: 0.16, type: "triangle" },
    { frequency: 659.25, start: 0.09, duration: 0.16, type: "triangle" },
    { frequency: 783.99, start: 0.18, duration: 0.16, type: "triangle" },
    { frequency: 1046.5, start: 0.27, duration: 0.3, type: "triangle" },
  ] },
} satisfies Record<string, { label: string; description: string; notes: Note[] }>;

export type AlertSoundId = keyof typeof ALERT_SOUNDS;
export type AlertSoundSettings = { enabled: boolean; sound: AlertSoundId; volume: number };

export const DEFAULT_ALERT_SOUND_SETTINGS: AlertSoundSettings = { enabled: true, sound: "chime", volume: 0.6 };
const STORAGE_KEY = "wls.alertSound";
const CHANGE_EVENT = "wls:alert-sound-change";

/** Valide un réglage relu du stockage : une valeur corrompue retombe sur le défaut. */
export function parseAlertSoundSettings(raw: string | null): AlertSoundSettings {
  if (!raw) return DEFAULT_ALERT_SOUND_SETTINGS;
  try {
    const value = JSON.parse(raw) as Partial<AlertSoundSettings>;
    return {
      enabled: typeof value.enabled === "boolean" ? value.enabled : DEFAULT_ALERT_SOUND_SETTINGS.enabled,
      sound: typeof value.sound === "string" && value.sound in ALERT_SOUNDS ? value.sound : DEFAULT_ALERT_SOUND_SETTINGS.sound,
      volume: typeof value.volume === "number" && Number.isFinite(value.volume)
        ? Math.min(1, Math.max(0, value.volume)) : DEFAULT_ALERT_SOUND_SETTINGS.volume,
    };
  } catch { return DEFAULT_ALERT_SOUND_SETTINGS; }
}

/** Le réglage est propre à ce navigateur : chaque membre choisit son son sur son poste. */
export function loadAlertSoundSettings(): AlertSoundSettings {
  try { return parseAlertSoundSettings(localStorage.getItem(STORAGE_KEY)); }
  catch { return DEFAULT_ALERT_SOUND_SETTINGS; }
}

export function saveAlertSoundSettings(settings: AlertSoundSettings): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch { /* stockage indisponible */ }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function subscribeAlertSoundSettings(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => { if (event.key === STORAGE_KEY) onChange(); };
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onStorage);
  };
}

type AudioContextConstructor = new () => AudioContext;
let audioContext: AudioContext | undefined;

/** Synthétise le son sans fichier audio ; un navigateur peut le bloquer avant le premier clic. */
export async function playAlertSound(sound: AlertSoundId, volume: number): Promise<void> {
  try {
    if (volume <= 0) return;
    const AudioContextClass = window.AudioContext
      ?? (window as typeof window & { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext;
    if (!AudioContextClass) return;
    if (!audioContext || audioContext.state === "closed") audioContext = new AudioContextClass();
    if (audioContext.state === "suspended") await audioContext.resume();
    if (audioContext.state !== "running") return;

    const now = audioContext.currentTime + 0.01;
    // Courbe quadratique : la moitié du curseur doit s'entendre comme la moitié du volume.
    const peak = 0.3 * volume * volume;
    for (const note of ALERT_SOUNDS[sound].notes as Note[]) {
      const start = now + note.start;
      const end = start + note.duration;
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      oscillator.type = note.type ?? "sine";
      oscillator.frequency.setValueAtTime(note.frequency, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak * (note.gain ?? 1)), start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start(start);
      oscillator.stop(end + 0.02);
    }
  } catch {
    // Le blocage automatique du son par le navigateur ne doit jamais interrompre la surveillance.
  }
}

export async function playWatchAlertSound(): Promise<void> {
  const settings = loadAlertSoundSettings();
  if (settings.enabled) await playAlertSound(settings.sound, settings.volume);
}
